import { Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import React, { FC } from "react";
import { useStyles } from "src/theme/styles";
import { useTranslation } from "react-i18next";
import { WebchatEntryPointSetupMappingsContainerProps } from "src/types/textEntryPoints";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import {
	CustomTextareaAutosize,
	CustomTextField,
} from "../shared/CustomTextField";

const copyToClipboardTranslationKey: string = "TextEntryPoint.CopyToClipboard";

const WebchatEntryPointSetupInformationContainer: FC<WebchatEntryPointSetupMappingsContainerProps> =
	React.forwardRef((props, ref) => {
		const mappingProps = props[
			"entryPointMappingsContainerProps"
		] as WebchatEntryPointSetupMappingsContainerProps;
		const classes = useStyles();
		const { t } = useTranslation();

		const previewDisabled =
			!mappingProps.previewUrl ||
			mappingProps.dirty ||
			mappingProps.isSubmitting;

		const copyToClipboard = (text: string) => {
			navigator.clipboard.writeText(text);
		};

		return (
			<Box className={classes.entryPointSectionContainer}>
				<Grid item container xs={12} spacing={2}>
					<Grid
						item
						xs={12}
						className={classes.entryPointContainerInfo}
					>
						{t("TextEntryPoint.SetupInformationExplanations")}
					</Grid>
					<Grid
						item
						xs={12}
						className={classes.entryPointContainerInfo}
					>
						{t("TextEntryPoint.WebhookUrlExplanations")}
					</Grid>
					<Grid item xs={11}>
						<CustomTextField
							size="small"
							color="info"
							fullWidth
							label={t("TextEntryPoint.WebhookUrl")}
							name="webhookUrl"
							variant="outlined"
							data-testid="webhookUrl"
							id="webhookUrl"
							disabled
							value={mappingProps.webhookUrl}
							InputProps={{
								readOnly: true,
							}}
						/>
					</Grid>
					<Grid item xs={1}>
						<IconButton
							className={classes.deleteButton}
							onClick={() =>
								copyToClipboard(mappingProps.webhookUrl)
							}
							title={t(copyToClipboardTranslationKey)}
						>
							<ContentCopyIcon />
						</IconButton>
					</Grid>
					<Grid
						item
						xs={12}
						className={classes.entryPointContainerInfo}
					>
						{t("TextEntryPoint.ScriptExplanations")}
					</Grid>
					<Grid item xs={11}>
						<CustomTextField
							size="small"
							color="info"
							fullWidth
							label={t("TextEntryPoint.Script")}
							name="script"
							variant="outlined"
							data-testid="script"
							id="script"
							disabled
							value={mappingProps.script}
							InputProps={{
								readOnly: true,
							}}
						/>
					</Grid>
					<Grid
						item
						xs={1}
						className={classes.genericEntryPointRemoveButton}
					>
						<IconButton
							className={classes.deleteButton}
							onClick={() => copyToClipboard(mappingProps.script)}
							title={t(copyToClipboardTranslationKey)}
						>
							<ContentCopyIcon />
						</IconButton>
					</Grid>
					<Grid
						item
						xs={12}
						className={classes.entryPointContainerInfo}
					>
						{t("TextEntryPoint.ComponentExplanations")}
					</Grid>
					<Grid item xs={11}>
						<CustomTextareaAutosize
							readOnly
							className={classes.webchatComponentTextArea}
							sx={{
								backgroundColor: "transparent",
								color: "white",
							}}
							defaultValue={mappingProps.component}
						/>
					</Grid>
					<Grid item xs={1}>
						<IconButton
							className={classes.deleteButton}
							onClick={() =>
								copyToClipboard(mappingProps.component)
							}
							title={t(copyToClipboardTranslationKey)}
						>
							<ContentCopyIcon />
						</IconButton>
					</Grid>
					<Grid item xs={12} sx={{ marginTop: 2, display: "flex", alignItems: "center", gap: 1 }}>
						<Tooltip
							title={
								previewDisabled
									? t("TextEntryPoint.PreviewDisabledTooltip")
									: ""
							}
						>
							<span>
								<Button
									variant="contained"
									data-testid="preview"
									disabled={previewDisabled}
									onClick={() =>
										window.open(
											mappingProps.previewUrl,
											"_blank",
											"noopener"
										)
									}
								>
									{t("TextEntryPoint.Preview")}
								</Button>
							</span>
						</Tooltip>
						<Tooltip title={t("TextEntryPoint.PreviewInfoTooltip")}>
							<InfoIcon color="primary" fontSize="small" />
						</Tooltip>
					</Grid>
				</Grid>
			</Box>
		);
	});

export default WebchatEntryPointSetupInformationContainer;
